import React, { useState, useEffect } from 'react';
import s from './s.module.less';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { InsuranceSetting, CarrierSetting, TCarrier } from 'types/common';
import useInsurance from 'hooks/useInsurance';
import { Input, Checkbox } from 'antd';
import { DownOutlined, UpOutlined, SearchOutlined } from '@ant-design/icons';
import cls from 'classnames';

interface IStateInsuranceCard {
    state: string;
    insurance?: InsuranceSetting;
    onChange: (insurance: InsuranceSetting) => void;
    isOpen?: boolean;
    onToggle?: () => void;
    isOps?: boolean;
}

const StateInsuranceCard = (props: IStateInsuranceCard) => {
    const { state, insurance, onChange, isOpen = false, onToggle, isOps = false } = props;
    const [setting] = useSettings();
    const [insuranceList] = useInsurance(isOps);
    const [searchValue, setSearchValue] = useState('');
    const [selectedCarriers, setSelectedCarriers] = useState<CarrierSetting[]>([]);
    const [carrierExpanded, setCarrierExpanded] = useState<{ [key: string]: boolean }>({});
    const [otherCarriers, setOtherCarriers] = useState<string>('');

    const stateInfo = setting?.[EnumFields.STATE]?.find((item) => item.value === state);
    const stateInsuranceList = insuranceList.find((item) => item.state === state);

    useEffect(() => {
        if (insurance && insurance.carriers && insurance.carriers.length > 0) {
            setSelectedCarriers(insurance.carriers);
        } else {
            setSelectedCarriers([]);
        }
        setOtherCarriers(insurance?.otherCarriers || '');
    }, [insurance]);

    const toggleCarrierExpand = (carrierName: string) => {
        setCarrierExpanded({
            ...carrierExpanded,
            [carrierName]: !carrierExpanded[carrierName],
        });
    };

    const filteredCarriers: TCarrier[] =
        stateInsuranceList?.carriers.filter((carrier) =>
            carrier.name.toLowerCase().includes(searchValue.toLowerCase()),
        ) || [];

    const handleOnChange = (carriers: CarrierSetting[], otherCarriersName?: string) => {
        if (insurance) {
            onChange({
                ...insurance,
                carriers,
                otherCarriers: otherCarriersName ?? insurance.otherCarriers,
            });
        } else {
            onChange({
                state,
                carriers,
                otherCarriers: otherCarriersName ?? '',
            });
        }
    };

    const toggleCarrier = (carrier: TCarrier) => {
        const isSelected = selectedCarriers.some((c) => c.name === carrier.name);
        let newSelectedCarriers: CarrierSetting[];

        if (isSelected) {
            newSelectedCarriers = selectedCarriers.filter((c) => c.name !== carrier.name);
        } else {
            newSelectedCarriers = [...selectedCarriers, { ...carrier, plans: [], otherPlans: '' }];
            setCarrierExpanded({
                ...carrierExpanded,
                [carrier.name]: true,
            });
        }

        setSelectedCarriers(newSelectedCarriers);
        handleOnChange(newSelectedCarriers);
    };

    const togglePlan = (carrierName: string, plan: string) => {
        const newSelectedCarriers = selectedCarriers.map((carrier) => {
            if (carrier.name === carrierName) {
                const isSelected = carrier.plans.includes(plan);
                if (isSelected) {
                    return {
                        ...carrier,
                        plans: carrier.plans.filter((p) => p !== plan),
                    };
                } else {
                    return {
                        ...carrier,
                        plans: [...carrier.plans, plan],
                    };
                }
            }
            return carrier;
        });

        setSelectedCarriers(newSelectedCarriers);
        handleOnChange(newSelectedCarriers);
    };

    const selectAllPlans = (carrierName: string, plans: string[]) => {
        const newSelectedCarriers = selectedCarriers.map((carrier) => {
            if (carrier.name === carrierName) {
                return {
                    ...carrier,
                    plans: [...plans],
                };
            }
            return carrier;
        });

        setSelectedCarriers(newSelectedCarriers);
        handleOnChange(newSelectedCarriers);
    };

    const resetPlans = (carrierName: string) => {
        const newSelectedCarriers = selectedCarriers.map((carrier) => {
            if (carrier.name === carrierName) {
                return {
                    ...carrier,
                    plans: [],
                };
            }
            return carrier;
        });

        setSelectedCarriers(newSelectedCarriers);
        handleOnChange(newSelectedCarriers);
    };

    const addOtherPlans = (carrierName: string, plans: string) => {
        const newSelectedCarriers = selectedCarriers.map((carrier) => {
            if (carrier.name === carrierName) {
                return {
                    ...carrier,
                    otherPlans: plans,
                };
            }
            return carrier;
        });

        setSelectedCarriers(newSelectedCarriers);
        handleOnChange(newSelectedCarriers);
    };

    const addOtherCarriers = (carriers: string) => {
        setOtherCarriers(carriers);
        handleOnChange(selectedCarriers, carriers);
    };

    return (
        <div className={s.stateInsuranceCard}>
            <div className={s.header} onClick={onToggle}>
                <div className={s.state}>{stateInfo?.label}</div>
                <div className={s.expandIcon}>{isOpen ? <UpOutlined /> : <DownOutlined />}</div>
            </div>

            {isOpen && (
                <div className={s.content}>
                    <div className={s.searchWrapper}>
                        <Input
                            placeholder="Search carrier"
                            prefix={<SearchOutlined />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className={s.searchInput}
                        />
                    </div>

                    <div className={s.carrierCount}>{selectedCarriers.length} carriers selected</div>

                    <div className={s.carrierList}>
                        {filteredCarriers.map((carrier) => {
                            const isSelected = selectedCarriers.some((c) => c.name === carrier.name);
                            const isExpanded = carrierExpanded[carrier.name];
                            const selectedCarrier = selectedCarriers.find((c) => c.name === carrier.name);

                            return (
                                <div key={carrier.name} className={s.carrierItem}>
                                    <div
                                        className={cls(s.carrierHeader, { [s.selected]: isSelected })}
                                        onClick={() => {
                                            if (isSelected) {
                                                toggleCarrierExpand(carrier.name);
                                            } else {
                                                toggleCarrier(carrier);
                                            }
                                        }}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                toggleCarrier(carrier);
                                            }}
                                        />
                                        <span className={s.carrierName}>{carrier.name}</span>
                                        {selectedCarrier?.plans?.length ? (
                                            <span className={s.planCount}>
                                                {selectedCarrier?.plans.length}{' '}
                                                {selectedCarrier?.plans.length === 1 ? 'plan' : 'plans'}
                                            </span>
                                        ) : null}

                                        {isSelected && (
                                            <div
                                                className={s.expandButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleCarrierExpand(carrier.name);
                                                }}
                                            >
                                                {isExpanded ? <UpOutlined /> : <DownOutlined />}
                                                {isExpanded ? 'Hide plans' : 'Add plans'}
                                            </div>
                                        )}
                                    </div>

                                    {isSelected && isExpanded && (
                                        <div className={s.planSection}>
                                            {selectedCarrier?.plans?.length ? (
                                                <span className={s.carrierCount}>
                                                    {selectedCarrier?.plans.length}{' '}
                                                    {selectedCarrier?.plans.length === 1 ? 'plan' : 'plans'} selected
                                                </span>
                                            ) : null}
                                            <div className={s.planHeader}>
                                                <div className={s.planTitle}>Select the plans you accept</div>
                                                <div className={s.planActions}>
                                                    <Checkbox
                                                        checked={selectedCarrier?.plans.length === carrier.plans.length}
                                                        indeterminate={
                                                            selectedCarrier?.plans.length! > 0 &&
                                                            selectedCarrier?.plans.length! < carrier.plans.length
                                                        }
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                selectAllPlans(carrier.name, carrier.plans);
                                                            } else {
                                                                resetPlans(carrier.name);
                                                            }
                                                        }}
                                                    >
                                                        Select all
                                                    </Checkbox>
                                                    <div
                                                        className={s.resetButton}
                                                        onClick={() => resetPlans(carrier.name)}
                                                    >
                                                        Reset
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={s.planList}>
                                                {carrier.plans.map((plan) => {
                                                    const isPlanSelected = selectedCarrier?.plans.includes(plan);

                                                    return (
                                                        <div key={plan} className={s.planItem}>
                                                            <Checkbox
                                                                checked={isPlanSelected}
                                                                onChange={() => togglePlan(carrier.name, plan)}
                                                            >
                                                                {plan}
                                                            </Checkbox>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className={cls(s.planTitle, 'mt-2', 'mb-1')}>
                                                Other insurance plans not listed above
                                            </div>
                                            <Input
                                                placeholder="Add plans names and separate by comma "
                                                value={selectedCarrier?.otherPlans}
                                                onChange={(e) => addOtherPlans(carrier.name, e.target.value)}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className={cls(s.planTitle, 'mt-2', 'mb-1')}>Other insurance carriers not listed above</div>
                    <Input
                        placeholder="Add carrier names and separate by comma"
                        value={otherCarriers}
                        onChange={(e) => addOtherCarriers(e.target.value)}
                    />
                </div>
            )}
        </div>
    );
};

export default StateInsuranceCard;
